import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, Activity, Plus, Minus } from "lucide-react";
import "./ProductivityScoring.css";
import PublicHeader from "../../components/PublicHeader/PublicHeader";
import Testimonials from "../../components/Testimonials/Testimonials";
import ReadyToTransform from "../../components/ReadyToTransform/ReadyToTransform";
import Footer from "../../components/Footer/Footer";

const TITLE = "Real Time Productivity Scoring Software | TeamGrid";
const DESCRIPTION =
  "Measure real time productivity with live scores, team benchmarks and trend analysis. Identify performance gaps and support your team effectively.";
const CANONICAL = "https://teamgrid.ai/features/productivity-scoring";
const OG_IMAGE = "https://teamgrid.ai/og/og-home.webp";

const ProductivityScoring = () => {
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen] = useState({ 0: true });
  const toggleFaq = (i) => setFaqOpen((prev) => ({ ...prev, [i]: !prev[i] }));

  const teamMembers = [
    {
      name: "Ritesh Raghav",
      role: "Sr. Staff Developer",
      avatar: "RR",
      avatarGradient: "linear-gradient(135deg, #00D1C1 0%, #0D9488 100%)",
      scoreLabel: "95.34%",
      progressLabel: "87%",
      progressPct: 95,
      scoreColor: "#00D1C1",
      progressBarColor: "#00D1C1",
    },
    {
      name: "Apurv Patel",
      role: "Sr. Project Developer",
      avatar: "AP",
      avatarGradient: "linear-gradient(135deg, #A855F7 0%, #7E22CE 100%)",
      scoreLabel: "80.10%",
      progressLabel: "90%",
      progressPct: 80,
      scoreColor: "#F2673A",
      progressBarColor: "#F2673A",
    },
    {
      name: "Darshan Raval",
      role: "Sr. React Developer",
      avatar: "DR",
      avatarGradient: "linear-gradient(135deg, #00D1C1 0%, #0D9488 100%)",
      scoreLabel: "93.56%",
      progressLabel: "81%",
      progressPct: 93,
      scoreColor: "#00D1C1",
      progressBarColor: "#00D1C1",
    },
    {
      name: "Harsaj Chinta",
      role: "Sr. Project Developer",
      avatar: "HC",
      avatarGradient: "linear-gradient(135deg, #3B82F6 0%, #05A6F0 100%)",
      scoreLabel: "90.12%",
      progressLabel: "68%",
      progressPct: 90,
      scoreColor: "#00D1C1",
      progressBarColor: "#00D1C1",
    },
  ];

  return (
    <div className="productivity-scoring-container">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:site_name" content="TeamGrid" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={CANONICAL} />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={OG_IMAGE} />
      </Helmet>
      <div className="relative min-h-screen overflow-hidden bg-white">
        <PublicHeader />
        <div className="pt-16 sm:pt-18 md:pt-20">
          {/* Background Effects */}
          <div
            className="absolute inset-0"
            style={{
              background: "radial-gradient(ellipse at top, #FFFFFF, #FFFFFF)",
            }}
          ></div>

          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            {/* Hero: full-bleed gradient; content aligned with header */}
            <div
              className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen pt-16 sm:pt-18 md:pt-20 pb-10 sm:pb-12 md:pb-14 px-4 sm:px-6 lg:px-8"
              style={{
                background:
                  "radial-gradient(141.42% 141.42% at 0% 0%, rgba(230, 242, 255, 0.5) 0%, rgba(233, 252, 250, 0) 50%), radial-gradient(141.42% 141.42% at 100% 100%, rgba(240, 247, 255, 0.5) 0%, rgba(240, 247, 255, 0) 50%), #FFFFFF",
              }}
            >
              <div className="max-w-7xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="flex flex-col items-center w-full flex-none self-stretch"
                  style={{ padding: 0, gap: 20 }}
                >
                  <div
                    className="flex flex-row items-center rounded-full flex-none"
                    style={{
                      padding: "4px 12px",
                      gap: 8,
                      background: "rgba(0, 209, 193, 0.06)",
                      border: "1px solid rgba(0, 209, 193, 0.15)",
                      borderRadius: 9999,
                      boxSizing: "border-box",
                    }}
                  >
                    <TrendingUp
                      className="w-3 h-3 text-[#00D1C1] flex-shrink-0"
                      style={{ flex: "none" }}
                    />
                    <span
                      className="uppercase text-center text-[#00D1C1]"
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "0.6px",
                      }}
                    >
                      PERFORMANCE METRICS
                    </span>
                  </div>
                  <h1
                    className="text-center text-black flex-none mx-auto w-full max-w-[1760px]"
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 800,
                      fontSize: "clamp(2.5rem, 5vw, 62px)",
                      lineHeight: "clamp(50px, 8vw, 72px)",
                    }}
                  >
                    Measure What <span className="text-[#00D1C1]">Matters</span>
                  </h1>
                  <div className="flex flex-col items-center flex-none w-full max-w-[768px]">
                    <p
                      className="text-center flex-none"
                      style={{
                        fontFamily: "Inter, sans-serif",
                        fontWeight: 400,
                        fontSize: "20px",
                        lineHeight: "28px",
                        color: "rgba(0, 0, 0, 0.6)",
                      }}
                    >
                      Real-time productivity metrics for individuals and teams.
                      Know exactly who&apos;s performing and who needs support.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Main Feature Section - white bg, two columns */}
            <section className="flex flex-col justify-center items-center py-20 md:py-[80px] bg-white">
              <div className="w-full flex flex-col lg:flex-row justify-center items-center gap-12 lg:gap-16">
                {/* Left: Feature list */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  className="flex flex-col items-start gap-5 w-full lg:max-w-[608px]"
                >
                  <h2 className="text-3xl md:text-[48px] font-bold leading-[53px] text-black">
                    Measure What Matters
                  </h2>
                  <p className="text-[17px] leading-6 text-black/60">
                    Our scoring algorithm weighs multiple factors including
                    active time, focus duration, project contributions, and
                    collaboration patterns to give you a complete picture.
                  </p>

                  <div className="flex flex-col items-start pt-6 gap-8 relative">
                    {/* Timeline item 1 */}
                    <div className="flex flex-row items-start gap-6 w-full relative">
                      <div
                        className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center bg-[#E0FFFC] border-4 border-white"
                        style={{
                          boxShadow: "0px 0px 0px 2px rgba(0, 209, 193, 0.5)",
                        }}
                      >
                        <Activity className="w-5 h-5 text-[#00D1C1]" />
                      </div>
                      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                        <h3 className="text-[15px] font-bold leading-7 text-black">
                          Live Scores
                        </h3>
                        <p className="text-[15px] leading-[23px] text-black/60">
                          See productivity scores update in real-time throughout
                          the day
                        </p>
                      </div>
                    </div>
                    {/* Timeline item 2 */}
                    <div className="flex flex-row items-start gap-6 w-full relative">
                      <div className="flex-shrink-0 w-11 h-11 rounded-full overflow-hidden flex items-center justify-center">
                        <img
                          src="/Background+Border+Shadow_new_20.webp"
                          alt="Team Benchmarks"
                          className="w-11 h-11 object-contain"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                        <h3 className="text-[15px] font-bold leading-7 text-black">
                          Team Benchmarks
                        </h3>
                        <p className="text-[15px] leading-[23px] text-black/60">
                          Compare performance against team averages
                        </p>
                      </div>
                    </div>
                    {/* Timeline item 3 */}
                    <div className="flex flex-row items-start gap-6 w-full">
                      <div
                        className="flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center bg-[#E0FFFC] border-4 border-white"
                        style={{
                          boxShadow: "0px 0px 0px 2px rgba(0, 209, 193, 0.5)",
                        }}
                      >
                        <TrendingUp className="w-5 h-5 text-[#00D1C1]" />
                      </div>
                      <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                        <h3 className="text-[15px] font-bold leading-7 text-black">
                          Trend Analysis
                        </h3>
                        <p className="text-[15px] leading-[23px] text-black/60">
                          Track productivity trends over days, weeks, and months
                        </p>
                      </div>
                    </div>
                  </div>

                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => navigate("/book-demo")}
                    className="mt-6 px-8 py-3 bg-[#00D1C1] text-black font-bold text-base rounded-xl shadow-[0px_0px_15px_rgba(0,209,193,0.3)] hover:opacity-95 transition-opacity"
                  >
                    See Team Guides
                  </motion.button>
                </motion.div>

                {/* Right: Performance dashboard card */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  className="w-full lg:max-w-[608px] flex flex-col items-start p-8 gap-8 bg-[#FAF5FF] border border-[#EDF4FF] rounded-3xl shadow-[0px_20px_40px_-5px_rgba(15,23,42,0.1)]"
                >
                  <div className="flex flex-row justify-between items-center w-full">
                    <div className="flex flex-col">
                      <h3 className="text-lg font-bold leading-7 text-black">
                        Team Performance
                      </h3>
                      <span className="text-sm leading-5 text-black/60">
                        16 members • Today
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate("/book-demo")}
                      className="text-sm font-medium text-[#00D1C1] hover:underline"
                    >
                      View Details
                    </button>
                  </div>

                  <div className="flex flex-col gap-5 w-full">
                    {teamMembers.map((member, idx) => (
                      <div
                        key={idx}
                        className="flex flex-col gap-3 p-6 bg-white rounded-2xl shadow-[0px_4px_20px_-2px_rgba(0,0,0,0.08)]"
                      >
                        <div className="flex flex-row justify-between items-center gap-4">
                          <div className="flex flex-row items-center gap-3">
                            <div
                              className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                              style={{
                                background: member.avatarGradient,
                              }}
                            >
                              {member.avatar}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-bold leading-5 text-black">
                                {member.name}
                              </span>
                              <span className="text-xs leading-4 text-black/60">
                                {member.role}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end">
                            <span
                              className="text-2xl font-bold leading-8 text-right"
                              style={{ color: member.scoreColor }}
                            >
                              {member.scoreLabel}
                            </span>
                            <span className="text-xs leading-4 text-black/60">
                              {member.progressLabel}
                            </span>
                          </div>
                        </div>
                        <div className="w-full h-2 bg-[#EDF4FF] rounded overflow-hidden">
                          <div
                            className="h-full rounded bg-[#00D1C1]"
                            style={{
                              width: `${member.progressPct}%`,
                              backgroundColor: member.progressBarColor,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </motion.div>
              </div>
            </section>

            {/* Testimonials Section */}
            <Testimonials />

            {/* FAQ Section - full-bleed gray bg */}
            <div className="relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] w-screen py-16 bg-[#F3F4F6]">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-center mb-12"
                >
                  <span className="inline-block px-5 py-2.5 rounded-full text-xs font-semibold uppercase tracking-wide text-[#0D9488] bg-[#CCFBF1] border border-[#99F6E4] mb-6">
                    GOT QUESTIONS?
                  </span>
                  <h2 className="font-inter font-extrabold text-[42px] leading-[46px] md:text-[48px] md:leading-[52px] text-black mb-4">
                    Frequently Asked Questions
                  </h2>
                  <p className="text-lg text-gray-600">
                    Everything you need to know about TeamGrid for productivity
                    scoring
                  </p>
                </motion.div>

                <div className="space-y-4 w-full max-w-[720px] mx-auto">
                  {[
                    {
                      question:
                        "What is the Productivity Score, and how does it help?",
                      answer:
                        "Productivity Scoring turns complex work patterns into a simple performance metric that helps teams benchmark efficiency, identify trends, and coach improvement with data-backed insights.",
                    },
                  ].map((faq, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6, delay: 0.1 * index }}
                      className="w-full rounded-xl p-6 cursor-pointer bg-white"
                      style={{
                        border: faqOpen[index]
                          ? "1px solid #28B4AE"
                          : "1px solid #D1D5DB",
                        transition: "border-color 0.15s ease-in-out",
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => toggleFaq(index)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <h3
                          className="text-left font-bold text-[#1A1A1A] flex-1"
                          style={{
                            fontFamily: "Inter, sans-serif",
                            fontSize: 16,
                            lineHeight: "26px",
                          }}
                        >
                          {faq.question}
                        </h3>
                        <span className="flex-shrink-0">
                          {faqOpen[index] ? (
                            <Minus className="w-5 h-5 text-[#111111]" />
                          ) : (
                            <Plus className="w-5 h-5 text-[#111111]" />
                          )}
                        </span>
                      </button>
                      <AnimatePresence initial={false}>
                        {faqOpen[index] && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="overflow-hidden"
                          >
                            <p
                              className="mt-4"
                              style={{
                                fontFamily: "Inter, sans-serif",
                                fontWeight: 400,
                                fontSize: 16,
                                lineHeight: "26px",
                                color: "#374151",
                              }}
                            >
                              {faq.answer}
                            </p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>

            <ReadyToTransform />

            <Footer />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductivityScoring;
